import moment from "moment";

type FareDetail = {
  PAX_Type: number;
  Basic_Amount: number;
  AirportTax_Amount: number;
  Total_Amount: number;
  GST: number;
  Trade_Markup_Amount: number;
  Service_Fee_Amount: number;
};

type Flight = {
  Fares: {
    FareDetails: FareDetail[];
  }[];
};

type AirRepriceResponse = {
  Flight: Flight;
};

type PassengerCount = {
  ADULT: number;
  CHILD: number;
  INFANT: number;
};

export const calculateTotalJourneyDuration = (flightSegments: any) => {
  let totalDuration = 0;
  flightSegments?.forEach((segment: any, index: number) => {
    totalDuration += segment.duration || 0;
    if (index < flightSegments.length - 1) {
      totalDuration += flightSegments[index].cT || 0;
    }
  });
  return totalDuration;
};

export const calculateNewTotalJourneyDuration = (
  flightSegments: any
): number => {
  let totalDurationMinutes = 0;
  flightSegments?.forEach((segment: any, index: number) => {
    // Parse the segment duration
    const [hours, minutes] = segment.Duration.split(":").map(Number);
    totalDurationMinutes += hours * 60 + minutes;
    // Calculate layover time using moment for all but the last segment
    if (index < flightSegments.length - 1) {
      const currentArrival = moment(
        segment.Arrival_DateTime,
        "MM/DD/YYYY HH:mm"
      );
      const nextDeparture = moment(
        flightSegments[index + 1].Departure_DateTime,
        "MM/DD/YYYY HH:mm"
      );
      if (currentArrival.isValid() && nextDeparture.isValid()) {
        const layoverTimeMinutes = nextDeparture.diff(
          currentArrival,
          "minutes"
        );
        totalDurationMinutes += layoverTimeMinutes > 0 ? layoverTimeMinutes : 0;
      } else {
        console.error(`Invalid date format for segments:`, segment);
      }
    }
  });
  return totalDurationMinutes;
};

export const getMinPrice = (priceList: any[] = []) => {
  return Math.min(...priceList?.map((item) => item.fd.ADULT.fC.TF));
};

export const getNewMinPrice = (Fares: any[] = []) => {
  return Math.min(
    ...Fares?.map(
      (fare) => fare.FareDetails && fare.FareDetails[0]?.Total_Amount
    )
  );
};

export const getMinPriceObject = (fares: any) => {
  return fares?.reduce((min: any, flight: any) => {
    const totalPrice =
      flight.FareDetails && flight.FareDetails[0]?.Total_Amount;
    return totalPrice < min.FareDetails && min.FareDetails[0]?.Total_Amount
      ? flight
      : min;
  }, fares[0]);
};

export const calculateFareDetails = (
  response: AirRepriceResponse[],
  passengerCount: PassengerCount
) => {
  const totals = {
    adults: {
      basic: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
      count: passengerCount?.ADULT || 0,
      gst: 0,
    },
    children: {
      basic: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
      count: passengerCount?.CHILD || 0,
      gst: 0,
    },
    infants: {
      basic: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
      count: passengerCount?.INFANT || 0,
      gst: 0,
    },
    overall: {
      basic: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
      gst: 0,
    },
  };
  response?.forEach((segment) => {
    segment.Flight.Fares?.forEach((fare) => {
      fare.FareDetails?.forEach((detail) => {
        const {
          PAX_Type,
          Basic_Amount,
          AirportTax_Amount,
          Total_Amount,
          GST,
          Trade_Markup_Amount,
          Service_Fee_Amount,
        } = detail;
        if (PAX_Type === 0) {
          totals.adults.basic += Basic_Amount;
          totals.adults.taxes += AirportTax_Amount;
          totals.adults.serviceAmount += Service_Fee_Amount;
          totals.adults.trademarkupAmount += Trade_Markup_Amount;
          totals.adults.total += Total_Amount - Service_Fee_Amount;
          totals.adults.gst += GST;
        } else if (PAX_Type === 1) {
          totals.children.basic += Basic_Amount;
          totals.children.taxes += AirportTax_Amount;
          totals.adults.serviceAmount += Service_Fee_Amount;
          totals.adults.trademarkupAmount += Trade_Markup_Amount;
          totals.children.total += Total_Amount - Service_Fee_Amount;
          totals.children.gst += GST;
        } else if (PAX_Type === 2) {
          totals.infants.basic += Basic_Amount;
          totals.infants.taxes += AirportTax_Amount;
          totals.adults.serviceAmount += Service_Fee_Amount;
          totals.adults.trademarkupAmount += Trade_Markup_Amount;
          totals.infants.total += Total_Amount - Service_Fee_Amount;
          totals.infants.gst += GST;
        }
      });
    });
  });
  // Calculate overall totals
  totals.overall.basic =
    totals.adults.basic * totals.adults.count +
    totals.children.basic * totals.children.count +
    totals.infants.basic * totals.infants.count;
  totals.overall.taxes =
    totals.adults.taxes * totals.adults.count +
    totals.children.taxes * totals.children.count +
    totals.infants.taxes * totals.infants.count;
  totals.overall.serviceAmount =
    totals.adults.serviceAmount * totals.adults.count +
    totals.children.serviceAmount * totals.children.count +
    totals.infants.serviceAmount * totals.infants.count;
  totals.overall.trademarkupAmount =
    totals.adults.trademarkupAmount * totals.adults.count +
    totals.children.trademarkupAmount * totals.children.count +
    totals.infants.trademarkupAmount * totals.infants.count;
  totals.overall.gst =
    totals.adults.gst * totals.adults.count +
    totals.children.gst * totals.children.count +
    totals.infants.gst * totals.infants.count;
  totals.overall.total =
    totals.adults.total * totals.adults.count +
    totals.children.total * totals.children.count +
    totals.infants.total * totals.infants.count;
  return totals;
};

export const calculateFareDetailsFromReprint = (
  response: any[]
  // passengerCount: PassengerCount
) => {
  const totals = {
    adults: {
      basic: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
      gst: 0,
    },
    children: {
      basic: 0,
      gst: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
    },
    infants: {
      basic: 0,
      gst: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
    },
    overall: {
      basic: 0,
      taxes: 0,
      serviceAmount: 0,
      trademarkupAmount: 0,
      total: 0,
      gst: 0,
    },
  };
  response?.forEach((segment) => {
    segment.PAXTicketDetails?.forEach((pax: any) => {
      pax.Fares?.forEach((fare: any) => {
        fare.FareDetails?.forEach((detail: any) => {
          const {
            PAX_Type,
            Basic_Amount,
            AirportTax_Amount,
            Total_Amount,
            GST,
            Trade_Markup_Amount,
            Service_Fee_Amount,
          } = detail;
          if (PAX_Type === 0) {
            totals.adults.basic += Basic_Amount;
            totals.adults.taxes += AirportTax_Amount;
            totals.adults.serviceAmount += Service_Fee_Amount;
            totals.adults.trademarkupAmount += Trade_Markup_Amount;
            totals.adults.total += Total_Amount - Service_Fee_Amount;
            totals.adults.gst += GST;
          } else if (PAX_Type === 1) {
            totals.children.basic += Basic_Amount;
            totals.children.taxes += AirportTax_Amount;
            totals.adults.serviceAmount += Service_Fee_Amount;
            totals.adults.trademarkupAmount += Trade_Markup_Amount;
            totals.children.total += Total_Amount - Service_Fee_Amount;
            totals.children.gst += GST;
          } else if (PAX_Type === 2) {
            totals.infants.basic += Basic_Amount;
            totals.infants.taxes += AirportTax_Amount;
            totals.adults.serviceAmount += Service_Fee_Amount;
            totals.adults.trademarkupAmount += Trade_Markup_Amount;
            totals.infants.total += Total_Amount - Service_Fee_Amount;
            totals.infants.gst += GST;
          }
        });
      });
    });
  });
  // Calculate overall totals
  totals.overall.basic =
    totals.adults.basic + totals.children.basic + totals.infants.basic;
  totals.overall.taxes =
    totals.adults.taxes + totals.children.taxes + totals.infants.taxes;
  totals.overall.serviceAmount =
    totals.adults.serviceAmount +
    totals.children.serviceAmount +
    totals.infants.serviceAmount;
  totals.overall.trademarkupAmount =
    totals.adults.trademarkupAmount +
    totals.children.trademarkupAmount +
    totals.infants.trademarkupAmount;
  totals.overall.gst =
    totals.adults.gst + totals.children.gst + totals.infants.gst;
  totals.overall.total =
    totals.adults.total + totals.children.total + totals.infants.total;
  return totals;
};

export const calculatePromoDiscount = (
  promo: any,
  totalAmount: number
): number => {
  if (promo.DiscountType === "PER") {
    const percentDiscount = (totalAmount * promo.Value) / 100;
    return Math.min(percentDiscount, promo.MaxDiscount);
  } else if (promo.DiscountType === "RS") {
    const promoAmount = Math.min(promo.Value, promo.MaxDiscount);
    return Math.min(promoAmount, totalAmount);
  }
  return 0;
};

// export const calculateGiftCodeDiscount = (
//   giftCodeAmount: any,
//   totalAmount: number
// ): number => {
//   return Math.min(giftCodeAmount, totalAmount) || 0;
// };

export const calculateGiftCodeDiscount = (
  maxAmount: number,
  totalAmount: number
): number => {
  if (totalAmount > maxAmount) {
    return maxAmount;
  } else {
    return totalAmount;
  }
};

export const calculateGiftCodeDiscountForSubmit = (
  minAmount: number,
  maxAmount: number,
  totalAmount: number
): number => {
  if (totalAmount < minAmount) {
    return minAmount;
  } else if (totalAmount > maxAmount) {
    return maxAmount;
  } else {
    return totalAmount;
  }
};

export const addCancellationCharge = (
  paxes: any[],
  cancellationCharge: number,
  cancellationChargeGSTPer: number
) => {
  const nameFrequency: Record<string, number> = {};
  // Count how many times each passenger name occurs
  for (const pax of paxes) {
    const name = pax.passanger_name;
    if (name) {
      nameFrequency[name] = (nameFrequency[name] || 0) + 1;
    }
  }
  // Apply cancellation charges
  return paxes.map((pax) => {
    const name = pax.passanger_name;
    const isRepeated = nameFrequency[name] > 1;
    // Skip charge if paxType is 2
    if (pax.paxType === 2) return { ...pax };
    return {
      ...pax,
      cancellation_charge: isRepeated
        ? cancellationCharge / 2
        : cancellationCharge,
      cancellation_gst_per: cancellationChargeGSTPer,
    };
  });
};
